import type { GeoJSONFeature } from 'mapbox-gl'
import { action, computed, makeObservable, observable } from 'mobx'

import {
  IStackMembers,
  parseStackMembers,
} from '~/client/src/shared/map/locationStacks'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import Workflow from '~/client/src/shared/models/Workflow'

import { pinCoordKey } from '../pinCoordKey'
import { SharedEntityKind } from '../sharedPinPoints'
import { IStackListProps } from './StackList'
import { buildStackRows, stackSiblingOrder } from './stackRows'
import { ITaggedMap } from './taggedMaps'

// Everything the stack needs from the map it sits on. A pick does what a click on that object's
// own marker does, so the selections are the map's own.
export interface IMapObjectStackHost {
  getLocation: (id: string) => LocationBase | undefined
  workflowsAt: (coordKey: string) => Workflow[]
  sharedWorkflowsAt: (coordKey: string) => Workflow[]
  selectLocation: (itemId: string) => void
  selectWorkflow: (workflow: Workflow) => void
  selectSharedGroup: (coordKey: string) => void
  taggedMapsOf: (location: LocationBase) => ITaggedMap[]
  isLocationOnMap: (id: string) => boolean
  isMapSwitchLocked: () => boolean
  onScreenMapId: () => string
  isEditorIdle: () => boolean
}

export interface IOpenStack {
  mapId: string
  members: IStackMembers
}

// Locations on one coordinate, listed with the workflows pinned there from the marker that
// stands for them. The map's store owns it, so a map that leaves and comes back finds it as it
// was; the mounted map view attaches what it reads.
export default class MapObjectStackStore {
  @observable public open: IOpenStack | null = null
  // The last stack opened, kept through the close so a sliding drawer does not empty mid-slide.
  @observable private shown: IOpenStack | null = null
  @observable.ref private host: IMapObjectStackHost = null

  public constructor() {
    makeObservable(this)
  }

  @action.bound
  public attach(host: IMapObjectStackHost): () => void {
    this.host = host
    return action(() => {
      if (this.host === host) this.host = null
    })
  }

  @action.bound
  public openOf(feature: GeoJSONFeature): void {
    const members = parseStackMembers(feature.properties)
    if (!members || !this.host) return
    this.open = { mapId: this.host.onScreenMapId(), members }
    this.shown = this.open
  }

  // Derived rather than closed by watchers: another map on screen, a markup being drawn, or a
  // filter leaving fewer than two members hides the list, and it is back when they pass.
  @computed
  public get isShown(): boolean {
    const { open, host } = this
    return (
      !!open &&
      !!host &&
      open.mapId === host.onScreenMapId() &&
      host.isEditorIdle() &&
      this.locationIds.length >= 2
    )
  }

  @action.bound
  public close(): void {
    this.open = null
  }

  // Once the drawer has slid away: a kept stack would go on recomputing the closed list.
  @action.bound
  public forget(): void {
    if (!this.open) this.shown = null
  }

  @action.bound
  public reopen(open: IOpenStack): void {
    this.open = open
    this.shown = open
  }

  @computed
  private get listed(): IOpenStack | null {
    return this.open ?? this.shown
  }

  // Members still drawn: a filter or data change can take one off the map under an open list.
  @computed
  private get locationIds(): string[] {
    const ids = this.listed?.members.locationIds ?? []
    return this.host ? ids.filter(this.host.isLocationOnMap) : []
  }

  // Shared workflows remember their pin: they select through their own group.
  @computed
  private get workflows(): {
    list: Workflow[]
    sharedCoordKeyById: Map<string, string>
  } {
    const members = this.listed?.members
    const sharedCoordKeyById = new Map<string, string>()
    if (!members || !this.host) return { list: [], sharedCoordKeyById }
    // Near-co-located members keep their own points, and their Workflows are pinned there.
    const { points } = members
    const local = points.flatMap(point =>
      this.host.workflowsAt(pinCoordKey(point)),
    )
    const shared = points.flatMap(point => {
      const coordKey = pinCoordKey(point, SharedEntityKind.Workflow)
      const workflows = this.host.sharedWorkflowsAt(coordKey)
      workflows.forEach(w => sharedCoordKeyById.set(w.id, coordKey))
      return workflows
    })
    return {
      list: [...new Map([...local, ...shared].map(w => [w.id, w])).values()],
      sharedCoordKeyById,
    }
  }

  @computed
  public get listProps(): IStackListProps {
    const getLocation = this.getLocation
    const { locationIds } = this
    return {
      rows: buildStackRows({
        locationIds,
        parentIdOf: id => getLocation(id)?.parent?.parentId,
        compare: stackSiblingOrder(locationIds, getLocation),
      }),
      getLocation,
      workflows: this.workflows.list,
      onSelectLocation: this.selectLocation,
      onSelectWorkflow: this.selectWorkflow,
      taggedMapsOf: this.taggedMapsOf,
      isMapSwitchLocked: !!this.host?.isMapSwitchLocked(),
      onClose: this.close,
    }
  }

  private getLocation = (id: string): LocationBase | undefined =>
    this.host?.getLocation(id)

  private taggedMapsOf = (location: LocationBase): ITaggedMap[] =>
    this.host?.taggedMapsOf(location) ?? []

  @action.bound
  private selectLocation(itemId: string): void {
    this.close()
    this.host?.selectLocation(itemId)
  }

  @action.bound
  private selectWorkflow(workflow: Workflow): void {
    const sharedCoordKey = this.workflows.sharedCoordKeyById.get(workflow.id)
    this.close()
    if (sharedCoordKey) {
      this.host?.selectSharedGroup(sharedCoordKey)
    } else {
      this.host?.selectWorkflow(workflow)
    }
  }
}
